import { useAuth0 } from '@auth0/auth0-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  DashboardPage_CulturePlate,
  ObservationMetric,
} from '~/api/backend/pages/dashboardPageAPI'
import MinimalBoldButton from '~/components/buttons/MinimalBoldButton'
import { FullPageError } from '~/components/Errors'
import Input from '~/components/Input'
import Link from '~/components/Link'
import LoadingMessage from '~/components/LoadingMessage'
import TinyMicroplate from '~/components/TinyMicroplate'
import {
  SUPPORTED_PLATE_DIMENSIONS,
  type supportedPlateFormats,
} from '~/components/TinyMicroplate.interface'
import { analytics } from '~/core/analytics'
import { useFeatureFlag } from '~/core/featureFlags'
import { useDemoQuery } from '~/demoControls/DemoContext'
import { getCellConcentrationColor } from '~/pages/Monitor/utils/cellConcentrationColors'
import { convertWellCoordsToWellName } from '~/utils/microplate'
import CellConcentrationHistogram from '../components/CellConcentrationHistogram'
import ConfluenceHistogram from '../components/ConfluenceHistogram'
import { OVER_CONFLUENT_THRESHOLD } from '../events/ViewModels/CultureViewModel'
import type { Metrics } from '../metrics'
import { useMetrics } from '../metrics'
import {
  DEAD_CULTURE_COLOR,
  EMPTY_CULTURE_COLOR,
  getConfluencyColor,
  OVER_CONFLUENT_COLOR,
} from '../utils/confluencyColors'
import cs from './dashboard.scss'

export default function Dashboard() {
  const minLastCheckedOut = useMemo(
    () => dayjs().subtract(2, 'weeks').startOf('day').toISOString(),
    [],
  )

  const hackathon2025 = useFeatureFlag('hackathon2025') ?? false
  const requestedMetrics = useMemo<ObservationMetric[]>(
    () => (hackathon2025 ? ['cell_concentration'] : ['confluence']),
    [hackathon2025],
  )

  const demoData = useDemoQuery('monitor', 'Dashboard')?.()
  const backendClient = useBackendClient()

  // Use React Query's useInfiniteQuery for automatic pagination
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey: ['dashboard', minLastCheckedOut, requestedMetrics.join(',')],
      queryFn: async ({ pageParam }) => {
        return await backendClient.dashboardPage.getData(
          minLastCheckedOut,
          pageParam === undefined ? 24 : 32, // First page: 24, subsequent: 32
          pageParam,
          requestedMetrics,
        )
      },
      getNextPageParam: lastPage => {
        // Defensive check: Only return next token if backend says hasMore AND we got plates
        const hasPlates =
          lastPage.activePlates.length + lastPage.recentlyInactivePlates.length > 0
        return lastPage.hasMore && hasPlates ? lastPage.nextPageToken : undefined
      },
      enabled: !demoData, // Skip query if demo data exists
      refetchInterval: 2 * 60 * 1000, // Poll every 2 minutes
    })

  // Flatten pages into arrays
  const allActivePlates = useMemo(
    () => data?.pages.flatMap(page => page.activePlates) ?? [],
    [data],
  )
  const allInactivePlates = useMemo(
    () => data?.pages.flatMap(page => page.recentlyInactivePlates) ?? [],
    [data],
  )

  // Handle demo data
  if (demoData != null) {
    return (
      <Content
        activePlates={demoData.activePlates}
        recentlyInactivePlates={demoData.recentlyInactivePlates}
        hasMore={demoData.hasMore ?? false}
        isLoadingMore={false}
        onLoadMore={() => {}}
      />
    )
  }

  if (isLoading) {
    return <LoadingPlaceholder />
  }
  if (error) {
    return <FullPageError error={error as Error} />
  }

  return (
    <Content
      activePlates={allActivePlates}
      recentlyInactivePlates={allInactivePlates}
      hasMore={hasNextPage ?? false}
      isLoadingMore={isFetchingNextPage}
      onLoadMore={fetchNextPage}
    />
  )
}

type Plate = DashboardPage_CulturePlate

function Content(props: {
  activePlates: Plate[]
  recentlyInactivePlates: Plate[]
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}) {
  const hackathon2025 = useFeatureFlag('hackathon2025')
  const metrics = useMetrics()
  const { hasMore, isLoadingMore, onLoadMore } = props
  const hasTrackedPageView = useRef(false)
  const previousFilterLengthRef = useRef<number>(0)

  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  useEffect(() => {
    if (hasTrackedPageView.current) return
    hasTrackedPageView.current = true
    try {
      analytics.page('Monitor', 'Dashboard', {
        activePlatesCount: props.activePlates.length,
        recentlyInactivePlatesCount: props.recentlyInactivePlates.length,
      })
    } catch (error) {
      // Never let telemetry break the dashboard UI.
      console.warn('Failed to track dashboard pageview', error)
    }
  }, [props.activePlates.length, props.recentlyInactivePlates.length])

  // Load more when trigger comes into view
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      onLoadMore()
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore])

  const currentHour = dayjs().hour()
  let greeting =
    currentHour < 5
      ? 'Good evening'
      : currentHour < 12
        ? 'Good morning'
        : currentHour < 17
          ? 'Good afternoon'
          : 'Good evening'

  const { user } = useAuth0()
  if (user) {
    let name = user.given_name || user.nickname || user.name
    if (name) {
      name = name?.slice(0, 1).toLocaleUpperCase() + name.slice(1)
      greeting += `, ${name}`
    }
  }

  const [filter, setFilter] = useState('')
  const handleFilterChange = (value: string) => {
    const previousValueLength = previousFilterLengthRef.current
    const nextValueLength = value.length
    setFilter(value)
    // Avoid logging raw barcode text; track only whether the filter was used/cleared.
    if (previousValueLength === 0 && nextValueLength > 0) {
      try {
        metrics.dashboard.filterApplied(nextValueLength)
      } catch {
        // Telemetry must never break typing in the filter input.
      }
    }
    if (previousValueLength > 0 && nextValueLength === 0) {
      try {
        metrics.dashboard.filterCleared(previousValueLength)
      } catch {
        // Telemetry must never break typing in the filter input.
      }
    }
    previousFilterLengthRef.current = nextValueLength
  }
  const filterLowercase = filter.toLowerCase()

  const activePlates = props.activePlates.filter(plate =>
    plate.barcode.toLowerCase().includes(filterLowercase),
  )

  const recentlyInactivePlates = props.recentlyInactivePlates.filter(plate =>
    plate.barcode.toLowerCase().includes(filterLowercase),
  )

  const cutoffToday = dayjs().startOf('day').toISOString()
  const cutoff7Days = dayjs().subtract(7, 'days').startOf('day').toISOString()
  const platesCheckedOutToday: Plate[] = []
  const platesCheckedOutLast7Days: Plate[] = []
  const platesCheckedOutLast14Days: Plate[] = []
  for (const plate of recentlyInactivePlates) {
    if (plate.checkedOutAt && plate.checkedOutAt > cutoffToday) {
      platesCheckedOutToday.push(plate)
    } else if (plate.checkedOutAt && plate.checkedOutAt > cutoff7Days) {
      platesCheckedOutLast7Days.push(plate)
    } else {
      platesCheckedOutLast14Days.push(plate)
    }
  }

  return (
    <div className={cs.content}>
      <h1 className={cs.title}>{greeting}</h1>

      <div className={cs.header}>
        <div className={cs.filterSection}>
          <Input
            className={cs.filterControl}
            inputClassName={cs.input}
            placeholder='Barcode...'
            label='Filter by Barcode'
            value={filter}
            onChange={handleFilterChange}
            highlight={filter !== ''}
          />
          {hasMore && (
            <div className={cs.filterHint}>
              Filtering loaded plates only — scroll to load more
            </div>
          )}
        </div>
        <div className={cs.spacer}></div>
        {hackathon2025 && (
          <CellConcentrationHistogramWrapper activePlates={activePlates} />
        )}
        {!hackathon2025 && <ConfluenceHistogramWrapper activePlates={activePlates} />}
      </div>

      <div>
        <div className={cs.checkedInHeader}>
          <h2 className={cs.heading}>Checked In</h2>
        </div>
        {activePlates.length > 0 ? (
          <PlateGallery plates={activePlates} metrics={metrics} />
        ) : (
          <NoPlates />
        )}
      </div>

      <div>
        <div className={cs.recentlyCheckedOutHeader}>
          <h2 className={cs.heading}>Recently Checked Out</h2>
          <MinimalBoldButton
            label='Browse Older Plates'
            type='primary'
            className={cs.browseOlderPlatesButton}
            to='/cultures'
          />
        </div>

        {platesCheckedOutToday.length > 0 ? (
          <>
            <h3 className={cs.subheading}>Today</h3>
            <PlateGallery plates={platesCheckedOutToday} metrics={metrics} />
          </>
        ) : null}

        {platesCheckedOutLast7Days.length > 0 ? (
          <>
            <h3 className={cs.subheading}>Previous 7 days</h3>
            <PlateGallery plates={platesCheckedOutLast7Days} metrics={metrics} />
          </>
        ) : null}

        {platesCheckedOutLast14Days.length > 0 ? (
          <>
            <h3 className={cs.subheading}>Previous 14 days</h3>
            <PlateGallery plates={platesCheckedOutLast14Days} metrics={metrics} />
          </>
        ) : null}

        {recentlyInactivePlates.length === 0 ? <NoPlates /> : null}
      </div>

      {/* Infinite Scroll Trigger - Minimal and elegant */}
      {hasMore && (
        <div className={cs.loadMoreSection}>
          <div ref={ref} className={cs.scrollTrigger}>
            {isLoadingMore ? (
              <div className={cs.loadingIndicator}>
                <LoadingMessage label='Loading more plates...' />
              </div>
            ) : (
              <div className={cs.scrollHint}>
                <div className={cs.scrollIcon}>↓</div>
                <span className={cs.scrollText}>Scroll for more</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ConfluenceHistogramWrapper(props: {
  activePlates: DashboardPage_CulturePlate[]
}) {
  const activePlates = props.activePlates

  const allConfluencyValues = activePlates
    .flatMap((culturePlate: DashboardPage_CulturePlate) => culturePlate.wellCultures)
    .filter(
      wellCulture =>
        wellCulture.confluence != null &&
        wellCulture.custom_culture_status.category === 'active',
    )
    .map(wellCulture => wellCulture.confluence?.value ?? 0)

  if (allConfluencyValues.length === 0) return null

  return (
    <ConfluenceHistogram
      confluencyValues={allConfluencyValues}
      className={cs.confluenceHistogram}
      label='Confluence of Checked-In Cultures (%)'
    />
  )
}

function CellConcentrationHistogramWrapper(props: {
  activePlates: DashboardPage_CulturePlate[]
}) {
  const activePlates = props.activePlates

  const allCellConcentrationValues = activePlates
    .flatMap((culturePlate: DashboardPage_CulturePlate) => culturePlate.wellCultures)
    .filter(
      wellCulture =>
        wellCulture.cell_concentration != null &&
        wellCulture.custom_culture_status.category === 'active',
    )
    .map(wellCulture => wellCulture.cell_concentration ?? 0)

  if (allCellConcentrationValues.length === 0) return null

  return (
    <CellConcentrationHistogram
      cellConcentrationValues={allCellConcentrationValues}
      className={cs.confluenceHistogram}
      label='Cell Concentration of Checked-In Cultures'
    />
  )
}

function LoadingPlaceholder() {
  return <LoadingMessage className={cs.loadingMessage} label={'Loading...'} />
}

function PlateGallery({ plates, metrics }: { plates: Plate[]; metrics: Metrics }) {
  const fillerElements = useMemo(() => {
    const filler = plates.length % 8 === 0 ? 0 : 8 - (plates.length % 8)
    const elements: JSX.Element[] = []
    for (let i = 0; i < filler; i++) {
      elements.push(
        <div
          key={`plate-gallery-filler-${plates.length}-${i}`}
          className={cs.gridItem}
        ></div>,
      )
    }
    return elements
  }, [plates.length])

  return (
    <div className={cs.plateGallery}>
      {plates.map(plate => (
        <Plate
          key={plate.id}
          plate={plate}
          showAvgConfluence={true}
          metrics={metrics}
        />
      ))}
      {fillerElements}
    </div>
  )
}

function Plate({
  plate,
  showAvgConfluence,
  metrics,
}: {
  plate: Plate
  showAvgConfluence: boolean
  metrics: Metrics
}) {
  const hackathon2025 = useFeatureFlag('hackathon2025')
  const wellCultureByWell = useMemo(
    () => Object.fromEntries(plate.wellCultures.map(wc => [wc.well, wc])),
    [plate],
  )

  if (!plate) {
    return <div className={cs.gridItem}></div>
  }

  const { rows, columns } = plate.plateDimensions
  if (
    !SUPPORTED_PLATE_DIMENSIONS.some(
      supported => supported.rows === rows && supported.columns === columns,
    )
  ) {
    console.error(
      `Unsupported plate dimensions for TinyMicroplate: ${rows} x ${columns}`,
    )
    return null
  }
  const plateFormat = `wells_${rows * columns}`

  const content = (
    <>
      <TinyMicroplate
        plateFormat={plateFormat as supportedPlateFormats}
        highlights={[
          {
            colorRgbFn: (row, col) => {
              const wellName = convertWellCoordsToWellName(row, col)
              const wellCulture = wellCultureByWell[wellName]
              if (!wellCulture) {
                return EMPTY_CULTURE_COLOR
              }
              if (wellCulture.custom_culture_status.category === 'terminated') {
                return DEAD_CULTURE_COLOR
              }

              if (!hackathon2025) {
                const confluenceValue = wellCulture.confluence?.value ?? 0
                if (confluenceValue > OVER_CONFLUENT_THRESHOLD) {
                  return OVER_CONFLUENT_COLOR
                }
                return getConfluencyColor(confluenceValue)
              }

              const cellConcentration = wellCulture.cell_concentration ?? null
              return getCellConcentrationColor(cellConcentration)
            },
          },
        ]}
        size='medium'
        shouldAnimate
      />

      <div className={cs.plateName}>{plate.barcode}</div>

      {showAvgConfluence && !hackathon2025 ? <AverageConfluence plate={plate} /> : null}
      {showAvgConfluence && hackathon2025 ? (
        <AverageCellConcentration plate={plate} />
      ) : null}
    </>
  )

  return (
    <Link
      to={`/plate/${plate.id}`}
      key={plate.id}
      className={cx(cs.gridItem, cs.interactive)}
      onClick={() => {
        try {
          metrics.dashboard.plateClick(plate.id)
        } catch {
          // Telemetry must never block navigation.
        }
      }}
    >
      {content}
    </Link>
  )
}

function AverageConfluence({ plate }: { plate: Plate }) {
  const activeCultures = plate.wellCultures.filter(
    ({ custom_culture_status }) => custom_culture_status.category === 'active',
  )
  if (activeCultures.length === 0) {
    return (
      <div className={cx(cs.avgConfluence, cs.placeholder)}>
        <br />
      </div>
    )
  }

  const culturesToAverage = activeCultures.filter(
    ({ confluence }) => confluence != null,
  )
  if (culturesToAverage.length === 0) {
    return (
      <div
        className={cx(cs.avgConfluence, cs.placeholder)}
        title='No confluence data to show'
      >
        ND
      </div>
    )
  }

  const averageConfluence =
    culturesToAverage
      .map(wellCulture => wellCulture.confluence?.value ?? 0)
      .reduce((sum, value) => sum + value, 0) / culturesToAverage.length
  return (
    <div
      className={cs.avgConfluence}
      style={{ color: getConfluencyColor(averageConfluence, true) }}
      title={`Average confluence of ${culturesToAverage.length} wells`}
    >
      {Math.round(averageConfluence)}%
    </div>
  )
}

function AverageCellConcentration({ plate }: { plate: Plate }) {
  const activeCultures = plate.wellCultures.filter(
    ({ custom_culture_status }) => custom_culture_status.category === 'active',
  )
  if (activeCultures.length === 0) {
    return (
      <div className={cx(cs.avgConfluence, cs.placeholder)}>
        <br />
      </div>
    )
  }

  const culturesToAverage = activeCultures.filter(
    ({ cell_concentration }) => cell_concentration != null,
  )

  if (culturesToAverage.length === 0) {
    return (
      <div
        className={cx(cs.avgConfluence, cs.placeholder)}
        title='No cell concentration data to show'
      >
        ND
      </div>
    )
  }

  const averageCellConcentration =
    culturesToAverage
      .map(wellCulture => wellCulture.cell_concentration ?? 0)
      .reduce((sum, value) => sum + value, 0) / culturesToAverage.length

  return (
    <div
      className={cs.avgConfluence}
      style={{ color: getCellConcentrationColor(averageCellConcentration) }}
      title={`Average cell concentration of ${culturesToAverage.length} wells`}
    >
      {Math.round(averageCellConcentration)} cells/mL
    </div>
  )
}

function NoPlates() {
  return <div className={cs.noPlates}>No Plates</div>
}
